// Zal planında ağıllı yapışma (Photoshop kimi): kənarlar və mərkəzlər üst-üstə düşəndə,
// və ya aralıq otaqdakı başqa aralıqla eyni olanda masa ora yapışır, xətt və məsafə göstərilir.
const SNAP_DISTANCE = 8;
const MAX_GAP = 400;
const EPS = 0.5;

// axis: hərəkət oxu → [əvvəl, sonra, en kəsiyin başı, sonu]
const AXES = { x: ['l', 'r', 't', 'b'], y: ['t', 'b', 'l', 'r'] };

// Döndərilmiş masanın ekrandakı həqiqi çərçivəsi
export const boxOf = (t) => {
    const rad = (t.fp_rot * Math.PI) / 180;
    const c = Math.abs(Math.cos(rad));
    const s = Math.abs(Math.sin(rad));
    const w = t.fp_w * c + t.fp_h * s;
    const h = t.fp_w * s + t.fp_h * c;
    const cx = t.fp_x + t.fp_w / 2;
    const cy = t.fp_y + t.fp_h / 2;
    return { l: cx - w / 2, r: cx + w / 2, t: cy - h / 2, b: cy + h / 2 };
};

export const unionBox = (boxes) => ({
    l: Math.min(...boxes.map((b) => b.l)),
    r: Math.max(...boxes.map((b) => b.r)),
    t: Math.min(...boxes.map((b) => b.t)),
    b: Math.max(...boxes.map((b) => b.b)),
});

export const shiftBox = (b, dx, dy) => ({ l: b.l + dx, r: b.r + dx, t: b.t + dy, b: b.b + dy });

// Bu oxda ən yaxın qonşular (en kəsiyi üst-üstə düşənlər)
const neighbors = (box, others, axis) => {
    const [lo, hi, cl, ch] = AXES[axis];
    const row = others.filter((o) => o !== box && o[cl] < box[ch] && o[ch] > box[cl]);
    const before = row.filter((o) => o[hi] <= box[lo] + EPS).sort((a, b) => b[hi] - a[hi])[0] || null;
    const after = row.filter((o) => o[lo] >= box[hi] - EPS).sort((a, b) => a[lo] - b[lo])[0] || null;
    return { before, after };
};

// Otaqda artıq olan aralıqlar: yanaşı duran hər cüt üçün
const pairGaps = (others, axis) => {
    const [lo, hi] = AXES[axis];
    const pairs = [];
    others.forEach((a) => {
        const { after } = neighbors(a, others, axis);
        if (!after) return;
        const gap = after[lo] - a[hi];
        if (gap >= 0 && gap <= MAX_GAP) pairs.push({ a, b: after, gap });
    });
    return pairs;
};

const midOf = (b, lo, hi) => (b[lo] + b[hi]) / 2;

// Bu ox üzrə lazım olan düzəliş (yapışacaq yer yoxdursa null)
const snapAxis = (box, others, axis) => {
    const [lo, hi] = AXES[axis];
    let best = null;
    const consider = (delta) => {
        if (Math.abs(delta) <= SNAP_DISTANCE && (best === null || Math.abs(delta) < Math.abs(best))) best = delta;
    };

    others.forEach((o) => {
        [box[lo], midOf(box, lo, hi), box[hi]].forEach((v) => {
            [o[lo], midOf(o, lo, hi), o[hi]].forEach((w) => consider(w - v));
        });
    });

    const { before, after } = neighbors(box, others, axis);
    pairGaps(others, axis).forEach(({ gap }) => {
        if (before) consider(before[hi] + gap - box[lo]);
        if (after) consider(after[lo] - gap - box[hi]);
    });
    if (before && after) {
        consider((before[hi] + after[lo]) / 2 - midOf(box, lo, hi)); // iki masanın düz ortası
    }
    return best;
};

export const snapDelta = (box, others, dx, dy) => {
    const moved = shiftBox(box, dx, dy);
    return { x: snapAxis(moved, others, 'x'), y: snapAxis(moved, others, 'y') };
};

const alignmentLines = (box, others) => {
    const lines = new Map();
    const add = (axis, pos, o) => {
        const [, , cl, ch] = AXES[axis];
        const key = `${axis}${Math.round(pos)}`;
        const cur = lines.get(key) || { axis, pos, from: box[cl], to: box[ch] };
        cur.from = Math.min(cur.from, o[cl]);
        cur.to = Math.max(cur.to, o[ch]);
        lines.set(key, cur);
    };
    ['x', 'y'].forEach((axis) => {
        const [lo, hi] = AXES[axis];
        others.forEach((o) => {
            [box[lo], midOf(box, lo, hi), box[hi]].forEach((v) => {
                [o[lo], midOf(o, lo, hi), o[hi]].forEach((w) => {
                    if (Math.abs(v - w) < EPS) add(axis, v, o);
                });
            });
        });
    });
    return [...lines.values()];
};

const gapSegment = (axis, a, b) => {
    const [lo, hi, cl, ch] = AXES[axis];
    const cross = (Math.max(a[cl], b[cl]) + Math.min(a[ch], b[ch])) / 2;
    return { axis, from: a[hi], to: b[lo], cross, gap: Math.round(b[lo] - a[hi]) };
};

// Çəkilən çərçivə üçün xətlər və məsafə yazıları
export const buildGuides = (box, others) => {
    const gaps = [];
    ['x', 'y'].forEach((axis) => {
        const pairs = pairGaps(others, axis);
        const { before, after } = neighbors(box, others, axis);
        [[before, box], [box, after]].forEach(([a, b]) => {
            if (!a || !b) return;
            const seg = gapSegment(axis, a, b);
            if (seg.gap > MAX_GAP) return;
            const same = pairs.filter((p) => Math.round(p.gap) === seg.gap);
            gaps.push({ ...seg, equal: same.length > 0 });
            same.slice(0, 4).forEach((p) => gaps.push({ ...gapSegment(axis, p.a, p.b), equal: true }));
        });
    });
    return { lines: alignmentLines(box, others), gaps };
};

// Düzləmə (Photoshop kimi): seçilənlərin ümumi çərçivəsinə görə. Qaytarır: [{ key, dx, dy }]
export const alignItems = (items, mode) => {
    const all = unionBox(items.map(boxOf));
    return items.map((t) => {
        const b = boxOf(t);
        const dx = {
            left: all.l - b.l,
            right: all.r - b.r,
            hcenter: (all.l + all.r) / 2 - (b.l + b.r) / 2,
        }[mode] ?? 0;
        const dy = {
            top: all.t - b.t,
            bottom: all.b - b.b,
            vcenter: (all.t + all.b) / 2 - (b.t + b.b) / 2,
        }[mode] ?? 0;
        return { key: t.key, dx, dy };
    });
};

// Bərabər məsafə: birinci və sonuncu yerində qalır, aradakılar elə düzülür ki, aralıqlar eyni olsun
export const distributeItems = (items, axis) => {
    const [lo, hi] = AXES[axis];
    const sorted = items.map((t) => ({ t, b: boxOf(t) })).sort((a, c) => (a.b[lo] + a.b[hi]) - (c.b[lo] + c.b[hi]));
    const first = sorted[0].b;
    const last = sorted[sorted.length - 1].b;
    const used = sorted.reduce((sum, { b }) => sum + (b[hi] - b[lo]), 0);
    const gap = (last[hi] - first[lo] - used) / (sorted.length - 1);
    let cursor = first[lo];
    return sorted.map(({ t, b }) => {
        const delta = cursor - b[lo];
        cursor += b[hi] - b[lo] + gap;
        return { key: t.key, dx: axis === 'x' ? delta : 0, dy: axis === 'y' ? delta : 0 };
    });
};
